import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import AppLayout from '@/components/AppLayout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import UsersTab from '@/components/settings/UsersTab';
import RechtenTab from '@/components/settings/RechtenTab';
import SessionsTab from '@/components/settings/SessionsTab';
import ActivityTab from '@/components/settings/ActivityTab';
import ToolsTab from '@/components/settings/ToolsTab';
import LocationsTab from '@/components/settings/LocationsTab';
import AccountTab from '@/components/settings/AccountTab';
import { Users, ShieldCheck, Monitor, Activity, Wrench, MapPin, UserCog } from 'lucide-react';

const TABS = [
  { id: 'gebruikers', label: 'Gebruikers', icon: Users, minRole: 'admin' },
  { id: 'rechten', label: 'Rechten', icon: ShieldCheck, minRole: 'owner' },
  { id: 'sessies', label: 'Sessies', icon: Monitor, minRole: 'owner' },
  { id: 'activiteitenlog', label: 'Activiteitenlog', icon: Activity, minRole: 'admin' },
  { id: 'tools', label: 'Tools', icon: Wrench, minRole: 'super_owner' },
  { id: 'locaties', label: 'Locaties', icon: MapPin, minRole: 'super_owner' },
  { id: 'account', label: 'Account', icon: UserCog, minRole: null },
];

export default function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasMinRole } = useAuth();
  
  const availableTabs = TABS.filter(t => !t.minRole || hasMinRole(t.minRole));
  const defaultTab = searchParams.get('tab') || availableTabs[0]?.id || 'account';
  const [activeTab, setActiveTab] = useState(defaultTab);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && availableTabs.some(t => t.id === tab)) {
      setActiveTab(tab);
    }
  }, [searchParams, availableTabs]);

  const handleTabChange = (val) => {
    setActiveTab(val);
    setSearchParams({ tab: val });
  };

  return (
    <AppLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#1a2332] tracking-tight">Instellingen</h1>
        <p className="text-sm text-slate-500 mt-1">Beheer gebruikers, rechten, sessies en meer.</p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} data-testid="settings-tabs-list">
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="bg-white border border-slate-200 p-1 h-auto flex-wrap sm:flex-nowrap gap-0.5 w-max sm:w-auto">
            {availableTabs.map(tab => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                title={tab.label}
                data-testid={`settings-tab-trigger-${tab.id}`}
                className="flex items-center gap-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1a2332] data-[state=active]:text-white rounded-md px-2.5 sm:px-3 py-1.5 transition-colors duration-150"
              >
                <tab.icon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{tab.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="mt-6" data-testid="settings-tab-content">
          <TabsContent value="gebruikers"><UsersTab /></TabsContent>
          <TabsContent value="rechten"><RechtenTab /></TabsContent>
          <TabsContent value="sessies"><SessionsTab /></TabsContent>
          <TabsContent value="activiteitenlog"><ActivityTab /></TabsContent>
          <TabsContent value="tools"><ToolsTab /></TabsContent>
          <TabsContent value="locaties"><LocationsTab /></TabsContent>
          <TabsContent value="account"><AccountTab /></TabsContent>
        </div>
      </Tabs>
    </AppLayout>
  );
}
